import { Button, Flex, Image, message, Modal, Spin, Typography } from "antd"
import { Alert } from "~/components/Alert"
import { useState } from "react"
import { useShallow } from "zustand/react/shallow"
import classNames from "classnames"
import { buildWhiteJpegDataUrl, dataUrlToFile } from "~/common/media/image"
import { getWhiteBgImagePointCost } from "~/common/billing/pointCosts"
import {
  PointCostText,
  usePointBalanceGuard,
} from "~/components/PointBalanceGuard"
import { useHotVideoStore } from "~/store/hotVideo"
import { useWhiteBgImageStore } from "~/store/whiteBgImage"
import { uploadFile } from "~/api/upload"
import styles from "./index.css"

const { Text } = Typography

type UploadPayload = {
  url: string
  name?: string
  size?: number
  type?: string
}

type WhiteBgImageModalProps = {
  onConfirm?: (url: string) => void
}

export const WhiteBgImageModal = ({ onConfirm }: WhiteBgImageModalProps) => {
  const [confirming, setConfirming] = useState(false)
  const [savingResource, setSavingResource] = useState(false)
  const [savedResourceUrl, setSavedResourceUrl] = useState("")
  const { runPointAction } = usePointBalanceGuard()
  const pointCost = getWhiteBgImagePointCost()
  const ensureHotVideoTaskId = useHotVideoStore((state) => state.ensureTaskId)
  const {
    closeModal,
    errorMessage,
    generateWhiteBgImage,
    loading,
    open,
    originImageUrl,
    resultUrl,
  } = useWhiteBgImageStore(
    useShallow((state) => ({
      closeModal: state.closeModal,
      errorMessage: state.errorMessage,
      generateWhiteBgImage: state.generateWhiteBgImage,
      loading: state.loading,
      open: state.open,
      originImageUrl: state.originImageUrl,
      resultUrl: state.resultUrl,
    })),
  )

  const saveWhiteBgImage = async (url: string) => {
    setSavingResource(true)

    try {
      const filename = `white-bg-image-${Date.now()}.jpg`
      const dataUrl = await buildWhiteJpegDataUrl(url)
      const file = dataUrlToFile(dataUrl, filename)
      const result = await uploadFile<UploadPayload>({ file })
      setSavedResourceUrl(result.data.url)
      return result.data.url
    } finally {
      setSavingResource(false)
    }
  }

  const handleGenerate = async () => {
    try {
      const generatedUrl = await runPointAction(pointCost, async () => {
        setSavedResourceUrl("")
        const hotVideoTaskId = await ensureHotVideoTaskId()
        if (!hotVideoTaskId) return undefined
        return generateWhiteBgImage({ hotVideoTaskId })
      })

      if (!generatedUrl) return

      await saveWhiteBgImage(generatedUrl)
      void message.success("白底图已生成并保存到我的资源")
    } catch (error) {
      void message.error(
        error instanceof Error ? error.message : "保存白底图失败",
      )
    }
  }

  const handleConfirm = async () => {
    if (!resultUrl || confirming || savingResource) return

    setConfirming(true)
    try {
      const resourceUrl =
        savedResourceUrl || (await saveWhiteBgImage(resultUrl))
      onConfirm?.(resourceUrl)
      void message.success("已替换为白底图")
      closeModal()
    } catch (error) {
      void message.error(error instanceof Error ? error.message : "替换失败")
    } finally {
      setConfirming(false)
    }
  }

  const handleCancel = () => {
    if (confirming || savingResource) return

    closeModal()
  }

  return (
    <>
      <Modal
        destroyOnHidden
        centered
        mask={{ closable: false }}
        open={open}
        title="AI生成白底图"
        width={880}
        onCancel={handleCancel}
        footer={[
          <Button
            disabled={confirming || savingResource}
            key="cancel"
            onClick={handleCancel}
          >
            取消
          </Button>,
          <Button
            disabled={!resultUrl || loading || savingResource}
            key="replace"
            loading={confirming || savingResource}
            type="primary"
            onClick={handleConfirm}
          >
            替换原图
          </Button>,
        ]}
      >
        <Flex
          className={styles.preview}
          gap={16}
          wrap
        >
          <Flex
            className={styles.imagePane}
            align="center"
            justify="center"
          >
            {originImageUrl ? (
              <Image
                alt="原图"
                className={styles.image}
                preview={{ mask: "预览原图" }}
                src={originImageUrl}
              />
            ) : null}
          </Flex>

          <Spin
            className={styles.resultSpin}
            spinning={loading || savingResource}
            description={loading ? "生成中" : "保存中"}
          >
            <Flex
              className={classNames(
                styles.imagePane,
                styles.resultPane,
                styles.resultSpinContent,
              )}
              align="center"
              justify="center"
            >
              {resultUrl ? (
                <Image
                  alt="白底图"
                  className={styles.image}
                  preview={{ mask: "预览白底图" }}
                  src={resultUrl}
                />
              ) : (
                <Flex
                  className={styles.placeholder}
                  align="center"
                  justify="center"
                  gap={12}
                  vertical
                >
                  <Text type="secondary">
                    点击按钮后会自动抠出主体并生成白底图
                  </Text>
                  <Button
                    loading={loading}
                    onClick={handleGenerate}
                    type="primary"
                  >
                    AI生成白底图
                  </Button>
                  <PointCostText cost={pointCost} />
                </Flex>
              )}
            </Flex>
          </Spin>
        </Flex>

        {errorMessage ? (
          <Alert
            className={styles.footerHint}
            title={errorMessage}
            showIcon
            level="strong"
          />
        ) : null}
      </Modal>
    </>
  )
}
